import {t} from "@lingui/core/macro";
import {useCallback} from "react";

import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {setPromptVariables} from "@khan/guide-core-helpers";
import {GuideStateApi} from "@khan/guide-state";
import {ActionType} from "@khan/guide-types";
import Log, {Errors} from "@khan/logging";

import {appendConversationWithErrorMessage} from "../internal/append-conversation-with-error-message.ts";
import {
    ResumeCooldownError,
    ResumeUnownedThreadError,
} from "../internal/errors.ts";
import {useCloseThreadMutation} from "../queries/use-close-thread-mutation.ts";

import {useInitializeGuide} from "./use-initialize-guide.ts";
import {useSendGuideEvent} from "./use-send-guide-event.ts";
import {useUnsetResumeThreadId} from "./use-unset-resume-thread-id.ts";

interface ResumeConversationFn {
    /**
     * Resumes a conversation with the AI guide for the given threadId.
     *
     * @returns A promise that resolves when the conversation has been resumed.
     */
    (threadId: string): Promise<void>;
}

/**
 * Obtain a function to resume a conversation for a given threadId.
 *
 * @returns A function to resume a conversation for a given threadId.
 */
export const useResumeConversation = (): ResumeConversationFn => {
    const sendGuideEvent = useSendGuideEvent();
    const closeThread = useCloseThreadMutation();
    const initializeGuide = useInitializeGuide();

    const unsetResumeThreadId = useUnsetResumeThreadId();

    const resumeConversationFn = useCallback(
        async (threadId: string) => {
            // Close current conversation if one exists
            const currentThreadId = GuideStateApi.latest().threadId;
            if (currentThreadId) {
                closeThread(currentThreadId);
            }

            // Set threadId so that it is passed with the event,
            // clear conversation, and set loading state
            GuideStateApi.set("threadId", threadId);
            GuideStateApi.set("conversation", []);
            GuideStateApi.set("conversationEntries", []);
            GuideStateApi.set("isLoading", true);

            try {
                await sendGuideEvent("resume_conversation", {
                    onResponseComplete: ({
                        conversation,
                        conversationEntries,
                        feedbackMap,
                        restoredPromptVariables,
                    }) => {
                        if (restoredPromptVariables) {
                            setPromptVariables(restoredPromptVariables);
                        }

                        if (conversation) {
                            GuideStateApi.set("conversation", conversation);
                        }

                        if (conversationEntries) {
                            GuideStateApi.set(
                                "conversationEntries",
                                conversationEntries,
                            );
                        }

                        GuideStateApi.set(
                            "ratedInteractions",
                            feedbackMap ?? new Map(),
                        );
                    },
                });
            } catch (error: any) {
                // If the user attempts to resume a thread they do not own,
                // clear the query param and initialize a new conversation
                if (error === ResumeUnownedThreadError) {
                    unsetResumeThreadId();

                    initializeGuide();

                    return;
                }

                // If the guide is on cooldown, don't show an error message
                // to the user. The guide will show a cooldown message instead.
                if (error === ResumeCooldownError) {
                    return;
                }

                // For any other errors, add an error message in Khanmigo
                // with an action bubble for starting a new chat
                GuideStateApi.set(
                    "conversation",
                    appendConversationWithErrorMessage(
                        [],
                        t`An error occurred while resuming this conversation. Refresh the page to try again, or start a new chat.`,
                        [
                            {
                                label: t`Start a new chat`,
                                command: "newChat",
                                actions: [
                                    {
                                        actionType: ActionType.newChat,
                                        parameters: {},
                                    },
                                ],
                            },
                        ],
                    ),
                );

                Log.error(
                    "Failed to send resume_conversation event to ai-guide.",
                    Errors.TransientKhanService,
                    {cause: error},
                );
            } finally {
                GuideStateApi.set("isLoading", false);
            }

            // Once response has been received, conversation
            // is initialized
            GuideStateApi.set("initialized", true);

            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideChatHistoryConversationResumed",
                eventSchemaVersion: 1,
                aiGuideThreadId: threadId,
                aiGuideNavigationId: GuideStateApi.latest().navigation.id,
            });
        },
        [closeThread, sendGuideEvent, unsetResumeThreadId, initializeGuide],
    );

    return resumeConversationFn;
};
